import { motion } from 'framer-motion'
import { Shield, Zap, Users, ArrowRight, CheckCircle, Star } from 'lucide-react'
// import { useTranslation } from 'react-i18next' // Not currently used, keeping for future i18n support
import { useScrollAnimation } from '@/hooks/useScrollAnimation'

const ArivLabs = () => {
  const { ref, controls, animationCapability } = useScrollAnimation({ threshold: 0.2 })
  // const { t } = useTranslation() // Not currently used, but keeping for future i18n support

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  }

  const services = [
    {
      icon: Shield,
      title: 'Cybersecurity Solutions',
      description: 'Comprehensive security architecture, threat detection, and compliance solutions for modern enterprises.',
      features: ['Security Architecture', 'Threat Detection', 'Compliance Solutions', 'Data Security Posture Management']
    },
    {
      icon: Users,
      title: 'SaaS Development',
      description: 'End-to-end SaaS platform development with scalable architecture and modern technology stacks.',
      features: ['Full-Stack Development', 'Cloud Architecture', 'API Development', 'DevOps & CI/CD']
    },
    {
      icon: Zap,
      title: 'Technical Leadership',
      description: 'Expert technical consulting and leadership to guide your engineering teams and architecture decisions.',
      features: ['Solution Architecture', 'Team Leadership', 'Technical Strategy', 'Code Reviews & Best Practices']
    }
  ]

  const principles = [
    { icon: Shield, title: 'Secure', description: 'Enterprise-grade security built into every solution' },
    { icon: Zap, title: 'Scalable', description: 'Architecture designed to grow with your business' },
    { icon: CheckCircle, title: 'Simple', description: 'Clean, maintainable code and intuitive user experiences' }
  ]

  // Fallback render without animations
  const renderArivLabsFallback = () => (
    <section id="arivlabs" className="py-20 bg-white dark:bg-gray-900" ref={ref}>
      <div className="container-max-width section-padding">
        <div>
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4" />
              My Company
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              ArivLabs
            </h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full mb-6" />
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-4 max-w-3xl mx-auto">
              <strong>Secure. Scalable. Simple</strong> - Boutique engineering services for startups and enterprises
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Get expert developers, architects, and technical leaders specializing in Cybersecurity and SaaS development.
            </p>
          </div>

          {/* Principles */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {principles.map((principle, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <principle.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {principle.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>

          {/* Services */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-8 rounded-xl card-hover">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 p-8 rounded-2xl">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Ready to Scale Your Engineering Team?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Let's discuss how ArivLabs can help you build secure, scalable, and simple solutions for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="btn-primary flex items-center gap-2 px-8 py-3 text-lg justify-center"
              >
                Get Your Quote
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="https://arivlabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2 px-8 py-3 text-lg justify-center"
              >
                Visit ArivLabs.com
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )

  // Use fallback only if animations aren't supported (reduced motion)
  if (animationCapability === 'none') {
    return renderArivLabsFallback()
  }

  return (
    <section id="arivlabs" className="py-20 bg-white dark:bg-gray-900 no-overflow" ref={ref}>
      <div className="container-max-width section-padding mobile-safe">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {/* Header */}
          <motion.div className="text-center mb-16" variants={itemVariants}>
            <motion.div 
              className="inline-flex items-center gap-2 bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-medium mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <Star className="w-4 h-4" />
              My Company
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              ArivLabs
            </h2>
            <div className="w-20 h-1 bg-primary-600 mx-auto rounded-full mb-6" />
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-4 max-w-3xl mx-auto">
              <strong>Secure. Scalable. Simple</strong> - Boutique engineering services for startups and enterprises
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Get expert developers, architects, and technical leaders specializing in Cybersecurity and SaaS development.
            </p>
          </motion.div>

          {/* Principles */}
          <motion.div className="grid md:grid-cols-3 gap-8 mb-16" variants={itemVariants}>
            {principles.map((principle, index) => (
              <motion.div
                key={index}
                className="text-center"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div 
                  className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <principle.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {principle.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {principle.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Services */}
          <motion.div className="grid lg:grid-cols-3 gap-8 mb-16" variants={itemVariants}>
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 dark:bg-gray-800 p-8 rounded-xl card-hover"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mb-6"
                  whileHover={{ rotate: 10 }}
                >
                  <service.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            className="text-center bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 p-8 rounded-2xl"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Ready to Scale Your Engineering Team?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Let's discuss how ArivLabs can help you build secure, scalable, and simple solutions for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="#contact"
                className="btn-primary flex items-center gap-2 px-8 py-3 text-lg justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Your Quote
                <ArrowRight className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="https://arivlabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2 px-8 py-3 text-lg justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Visit ArivLabs.com
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default ArivLabs
